<template>
  <ListItem
    :to="{
      name: 'editCarStatus',
      params: { id: car.id },
    }"
    :variant="getListItemVariant(car)"
  >
    <div class="flex flex-1 items-stretch gap-4">
      <div class="flex basis-[15%] flex-col justify-center rounded-sm bg-hazel-50 p-1 text-center">
        <p class="mb-1 font-medium" :class="{}">{{ callLabel }}</p>
        <div class="text-lg font-semibold">{{ startDate }}</div>
      </div>

      <div class="flex min-w-0 flex-1 gap-1 max-sm:flex-col md:items-center md:gap-4">
        <figure class="basis-[25%]">
          <img class="w-24" :src="imageUrl" />
        </figure>

        <div class="basis-[40%]">
          <div class="font-semibold">
            {{ car?.vehicleType?.make.name }}
            {{ car?.vehicleType?.model }}
          </div>
          <LicencePlate>{{ car?.licencePlate }}</LicencePlate>
        </div>

        <div
          class="relative flex h-11 basis-[35%] overflow-hidden rounded-sm bg-hazel-50 px-2 max-sm:hidden xl:h-[48px] xl:w-1/5"
        >
          <p class="clamp m-auto overflow-clip">
            {{ t('updateCarStatus') }}
          </p>
        </div>
      </div>
    </div>
  </ListItem>
</template>

<script setup lang="ts">
import type { Car } from '@/entities/cars/car.entity';
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';
import ListItem, { type ListItemVariant } from '@/components/ListItem.vue';
import LicencePlate from '@/components/LicencePlate.vue';
import { useFormattedDate } from '@/hooks/use-formatted-date';
import { DateTime } from 'luxon';
const props = defineProps<{
  car: Car;
  callLabel: string;
}>();

const { t } = useI18n();

const imageUrl = computed(() => props.car.imageUrl ?? props.car.vehicleType.imageUrl);

const startDate = props.car.nextAllocationStartDate
  ? useFormattedDate(props.car.nextAllocationStartDate, DateTime.TIME_24_SIMPLE)
  : '--:--';

function getListItemVariant(car: Car): ListItemVariant {
  const isToday =
    car.nextAllocationStartDate &&
    DateTime.fromISO(car.nextAllocationStartDate).startOf('day') <= DateTime.now().startOf('day');
  return isToday ? 'danger' : 'default';
}
</script>

<style scoped>
.clamp {
  -webkit-line-clamp: 2;
  display: -webkit-box;
  -webkit-box-orient: vertical;
}
</style>

<i18n lang="json">
{
  "en": {
    "detail": "Detail until",
    "vehicle": "Vehicle",
    "licencePlate": "Lp.",
    "color": "Color",
    "updateCarStatus": "Update Car Status"
  },
  "de": {
    "detail": "Aufbereiten bis",
    "vehicle": "Fahrzeug",
    "licencePlate": "Knz.",
    "color": "Farbe",
    "updateCarStatus": "Fahrzeugstatus aktualisieren"
  },
  "es": {
    "detail": "Detailing hasta",
    "vehicle": "Vehículo",
    "licencePlate": "Matr.",
    "color": "Color",
    "updateCarStatus": "Actualizar estado del vehículo"
  }
}
</i18n>
